import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function FAQ() {
  // State to track which accordion is open (allows only one to be open at a time)
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqs = [
    { 
      q: 'What is Diamond Dollar?', 
      a: 'Diamond Dollar is a next-generation stablecoin secured by GIA-certified diamonds. Each token maintains a 1:1 peg with the US Dollar, providing the stability of fiat currency with the security of physical diamond assets.' 
    },
    { 
      q: 'How is Diamond Dollar secured?', 
      a: 'Every Diamond Dollar token is secured by GIA-certified diamonds stored in secure, insured vaults. The assets are regularly audited and verified on-chain to ensure full transparency and 1:1 backing.' 
    },
    { 
      q: 'How can I acquire Diamond Dollar tokens?', 
      a: 'Diamond Dollar tokens are available through our official platform, major exchanges, and DeFi protocols. You can purchase tokens directly or mint them by depositing collateral.' 
    },
    { 
      q: 'What blockchain is Diamond Dollar built on?', 
      a: 'Diamond Dollar is built on Ethereum with ERC-20 token standards, with multi-chain deployment to ensure maximum accessibility and liquidity.' 
    },
    { 
      q: 'How is the 1:1 peg maintained?', 
      a: 'The 1:1 USD peg is maintained through a combination of collateral verification, smart contract automation, and market mechanisms. Our system ensures that the value of diamond collateral always exceeds the circulating token supply.' 
    },
    { 
      q: 'Is Diamond Dollar secure?', 
      a: 'Yes. Diamond Dollar utilizes audited smart contracts, multi-signature wallets, and institutional-grade security protocols. All transactions are transparent and verifiable on-chain.' 
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#050505', minHeight: '100vh', overflowX: 'hidden' }}>
      
      {/* ============ HERO SECTION ============ */}
      <Box
        sx={{
          position: 'relative',
          height: '70vh',
          minHeight: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          backgroundImage: 'url(/images/hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Dark Overlay */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.95) 100%)', 
          zIndex: 1 
        }} />
        
        {/* Slow Zoom */}
        <Box sx={{ 
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundImage: 'url(/images/hero.png)', backgroundSize: 'cover', backgroundPosition: 'center', 
          animation: 'slowZoom 25s ease-in-out infinite alternate', 
          '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
          zIndex: 0 
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10, pt: { xs: '140px', md: '180px' } }}>
          
          <Typography variant="h1" component="h1" sx={{
            fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: '3rem', sm: '3.5rem', md: '5rem' }, lineHeight: 1.1, mb: 4,
            background: 'linear-gradient(90deg, #ffffff 0%, #d4af37 50%, #ffffff 100%)', backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmer 6s linear infinite',
            filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.8))',
            '@keyframes shimmer': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-200% center' } },
          }}>
            Frequently Asked Questions
          </Typography>

          <Typography variant="body1" sx={{ color: '#e0e0e0', fontSize: { xs: '1rem', md: '1.2rem' }, fontWeight: 300, maxWidth: '700px', mx: 'auto', mb: 5, lineHeight: 1.8, textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
            Everything you need to know about Diamond Dollar, the world's leading diamond-secured stablecoin.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={RouterLink} to="/contact" variant="contained" endIcon={<ArrowForwardIcon />} sx={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)', 
              color: '#000000', 
              fontWeight: 800, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px', 
              boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)', 
              transition: 'all 0.5s ease',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e8c84a 0%, #d4af37 100%)', 
                color: '#000000', 
                boxShadow: '0 15px 50px rgba(212, 175, 55, 0.5)', 
                transform: 'translateY(-3px)' 
              },
            }}>
              Get Started
            </Button>
            <Button component={RouterLink} to="/technology" variant="outlined" sx={{
              borderColor: '#d4af37', 
              color: '#d4af37', 
              fontWeight: 700, 
              fontSize: '0.85rem', 
              letterSpacing: '2px', 
              textTransform: 'uppercase', 
              padding: '18px 40px', 
              borderRadius: '4px',
              '&:hover': { borderColor: '#ffffff', color: '#ffffff', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
            }}>
              View Technology
            </Button>
          </Stack>
        </Container>
      </Box>
      {/* ============ END HERO ============ */}

      {/* ============ MAIN CONTENT ============ */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Premium MUI Accordions - Gold Edition */}
        <Box>
          {faqs.map((item, index) => {
            const panel = `panel-${index}`;
            const isExpanded = expanded === panel;

            return (
              <Accordion
                key={index}
                disableGutters
                elevation={0}
                expanded={isExpanded}
                onChange={handleChange(panel)}
                sx={{
                  background: isExpanded 
                    ? 'linear-gradient(135deg, #141414 0%, #0a0a0a 100%)'
                    : 'linear-gradient(135deg, #0a0a0a 0%, #111111 100%)',
                  border: isExpanded ? '1px solid rgba(212, 175, 55, 0.5)' : '1px solid rgba(212, 175, 55, 0.15)',
                  borderRadius: '6px !important',
                  mb: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    display: 'none',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.05), transparent)',
                    pointerEvents: 'none',
                  },
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    borderColor: 'rgba(212, 175, 55, 0.4)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isExpanded ? '#d4af37' : '#a5a4a4', transition: 'transform 0.3s ease' }} />}
                  aria-controls={`${panel}-content`}
                  id={`${panel}-header`}
                  sx={{
                    padding: '25px 30px',
                    '& .MuiAccordionSummary-content': {
                      margin: '10px 0',
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: isExpanded ? '#d4af37' : '#ffffff',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {item.q}
                  </Typography>
                </AccordionSummary>
                
                <AccordionDetails sx={{ padding: '0 30px 30px 30px' }}>
                  <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.15)', mb: 3 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#a1a1a1',
                      lineHeight: 1.8,
                      fontSize: '1rem',
                    }}
                  >
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* Still have questions? */}
        <Box sx={{ textAlign: 'center', mt: 6, pt: 6, borderTop: '1px solid rgba(212, 175, 55, 0.1)' }}>
          <Typography variant="body2" sx={{ color: '#a1a1a1', mb: 2 }}>
            Still have questions? We're here to help.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderColor: '#d4af37',
              color: '#d4af37',
              '&:hover': {
                borderColor: '#ffffff',
                color: '#ffffff',
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
              },
            }}
          >
            Contact Our Team
          </Button>
        </Box>

      </Container>
    </Box>
  );
}

export default FAQ;